import {Form, Select} from 'antd';

import {FormActiveMark} from '../../../../../../component/form-active-mark/form-active-mark';
import {FormItemRadio} from '../../../../../../component/form-item-radio/form-item-radio';
import {Locale} from '../../../../../../provider/locale/locale';
import * as styles from '../reviews-filter.scss';
import {
    DELETED_REVIEWS_OPTIONS,
    HAS_NOTES_OPTIONS,
    HIDDEN_REVIEWS_OPTIONS,
    MODERATED_ANSWER_OPTIONS,
    REPLY_PUBLISHED_OPTIONS,
    REVIEW_CHANGED_OPTIONS,
    REVIEW_TYPE_OPTIONS,
    WAITING_FOR_A_SECOND_ANSWER_OPTIONS,
} from '../reviews-filter-const';
import {
    AbuseStatusEnum,
    AutoAbuseEnum,
    AutoReplayEnum,
    DeletedReviewsEnum,
    HasNotesEnum,
    HiddenReviewsEnum,
    ModeratedEnum,
    ReplyPublishedEnum,
    ReviewChangedEnum,
    ReviewNameEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
    WaitingForSecondAnswerEnum,
} from '../reviews-filter-type';

const {Option} = Select;

type PropsType = {
    form: ReturnType<typeof Form.useForm>[0];
    reviewFilterState: ReviewsFilterDataType;
    onHasNotesChange: (value: HasNotesEnum) => void;
    onHiddenReviewsChange: (value: HiddenReviewsEnum) => void;
    onDeletedReviewsChange: (value: DeletedReviewsEnum) => void;
    onAbuseStatusChange: (value: Array<AbuseStatusEnum>) => void;
    onAutoReplyChange: (value: Array<AutoReplayEnum>) => void;
    onAutoAbuseChange: (value?: AutoAbuseEnum | null) => void;
    onReviewChangedChange: (value: ReviewChangedEnum) => void;
    onWaitingForSecondAnswerChange: (value: WaitingForSecondAnswerEnum) => void;
    onReviewTypeChange: (value: ReviewNameEnum) => void;
    onModeratedReviewChange: (value: ModeratedEnum) => void;
    onReplyPublishedChange: (value: ReplyPublishedEnum) => void;
};

export function ReviewsFilterSectionAdditional(props: PropsType): JSX.Element {
    const {
        form,
        reviewFilterState,
        onHasNotesChange,
        onHiddenReviewsChange,
        onDeletedReviewsChange,
        onAbuseStatusChange,
        onAutoReplyChange,
        onAutoAbuseChange,
        onReviewChangedChange,
        onWaitingForSecondAnswerChange,
        onReviewTypeChange,
        onModeratedReviewChange,
        onReplyPublishedChange,
    } = props;

    return (
        <>
            <FormItemRadio<HasNotesEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.hasNotes]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__HAS_NOTES" />}
                name={ReviewsFilterFieldNameEnum.hasNotes}
                onClick={onHasNotesChange}
                options={HAS_NOTES_OPTIONS}
            />

            <FormItemRadio<HiddenReviewsEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.hiddenReviews]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__HIDDEN_REVIEW" />}
                name={ReviewsFilterFieldNameEnum.hiddenReviews}
                onClick={onHiddenReviewsChange}
                options={HIDDEN_REVIEWS_OPTIONS}
            />

            <FormItemRadio<DeletedReviewsEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.deletedReviews]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__DELETED_REVIEWS" />}
                name={ReviewsFilterFieldNameEnum.deletedReviews}
                onClick={onDeletedReviewsChange}
                options={DELETED_REVIEWS_OPTIONS}
            />

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.abuseStatus].length > 0} />
                <Form.Item
                    initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.abuseStatus]}
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__COMPLAINT_STATUS" />}
                    name={ReviewsFilterFieldNameEnum.abuseStatus}
                >
                    <Select<Array<AbuseStatusEnum>>
                        mode="multiple"
                        onChange={onAbuseStatusChange}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__LABEL__COMPLAINT_STATUS" />}
                        showSearch={false}
                    >
                        <Option value={AbuseStatusEnum.notSent}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_STATUS__NOT_SENT" />
                        </Option>

                        <Option value={AbuseStatusEnum.sent}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_STATUS__SENT" />
                        </Option>

                        <Option value={AbuseStatusEnum.accepted}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_STATUS__ACCEPTED" />
                        </Option>

                        <Option value={AbuseStatusEnum.declined}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_STATUS__DECLINED" />
                        </Option>
                    </Select>
                </Form.Item>
            </div>

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.autoReplay].length > 0} />
                <Form.Item
                    initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.autoReplay]}
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__AUTO_REPLIES" />}
                    name={ReviewsFilterFieldNameEnum.autoReplay}
                >
                    <Select<Array<AutoReplayEnum>>
                        mode="multiple"
                        onChange={onAutoReplyChange}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__AUTO_REPLIES" />}
                        showSearch={false}
                    >
                        <Option value={AutoReplayEnum.template}>
                            <Locale stringKey="REVIEWS__FILTER__AUTO_REPLY_VIA_TEMPLATE" />
                        </Option>

                        <Option value={AutoReplayEnum.neuralNetwork}>
                            <Locale stringKey="REVIEWS__FILTER__AUTO_REPLY_VIA_NEURAL_NETWORK" />
                        </Option>
                    </Select>
                </Form.Item>
            </div>

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark
                    isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.autoAbuse] !== AutoAbuseEnum.notSelected}
                />
                <Form.Item
                    getValueProps={(stored: AutoAbuseEnum | undefined) => {
                        if (stored === AutoAbuseEnum.yes || stored === AutoAbuseEnum.no) {
                            return {value: stored};
                        }

                        return {value: null};
                    }}
                    initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.autoAbuse]}
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__COMPLAINT_TYPE" />}
                    name={ReviewsFilterFieldNameEnum.autoAbuse}
                >
                    <Select<AutoAbuseEnum>
                        allowClear
                        onChange={onAutoAbuseChange}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__LABEL__COMPLAINT_TYPE" />}
                        showSearch={false}
                    >
                        <Option value={AutoAbuseEnum.yes}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_TYPE__AUTO" />
                        </Option>

                        <Option value={AutoAbuseEnum.no}>
                            <Locale stringKey="REVIEWS__FILTER__COMPLAINT_TYPE__MANUAL" />
                        </Option>
                    </Select>
                </Form.Item>
            </div>

            <FormItemRadio<ReviewChangedEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.reviewChanged]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__REVIEW_CHANGED" />}
                name={ReviewsFilterFieldNameEnum.reviewChanged}
                onClick={onReviewChangedChange}
                options={REVIEW_CHANGED_OPTIONS}
            />

            <FormItemRadio<WaitingForSecondAnswerEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.waitingForSecondAnswer]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__WAITING_FOR_A_SECOND_ANSWER" />}
                name={ReviewsFilterFieldNameEnum.waitingForSecondAnswer}
                onClick={onWaitingForSecondAnswerChange}
                options={WAITING_FOR_A_SECOND_ANSWER_OPTIONS}
            />

            <FormItemRadio<ReviewNameEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.reviewType]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__REVIEW_TYPE" />}
                name={ReviewsFilterFieldNameEnum.reviewType}
                onClick={onReviewTypeChange}
                options={REVIEW_TYPE_OPTIONS}
            />

            <FormItemRadio<ModeratedEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.reviewModerated]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__MODERATED" />}
                name={ReviewsFilterFieldNameEnum.reviewModerated}
                onClick={onModeratedReviewChange}
                options={MODERATED_ANSWER_OPTIONS}
            />

            <FormItemRadio<ReplyPublishedEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.replyPublished]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__OFFICIAL_RESPONSE_PUBLISHED" />}
                name={ReviewsFilterFieldNameEnum.replyPublished}
                onClick={onReplyPublishedChange}
                options={REPLY_PUBLISHED_OPTIONS}
            />
        </>
    );
}
